import { FC, useState } from "react";
import SalonManagementSoftware from "../../images/banner_images/best-salon-software.webp";
import salonMakeupImg from "../../images/banner_images/salon-makeup.webp";
import spaServiceImg from "../../images/banner_images/spa-service.webp";
import wellnessImg from "../../images/banner_images/wellness.webp";
import HeroSearchForm from "components/HeroSearchForm/HeroSearchForm";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";
import FeelaxoLogoMobileOnly from "../../images/feelaxo-log-white.webp";

interface SectionHeroProps {
  className?: string;
}

interface BusinessType {
  id: number;
  name: string;
  services: string[];
}

const SectionHero: FC<SectionHeroProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [showServices, setShowServices] = useState(false);
  const [selectedBusinessType, setSelectedBusinessType] = useState<number>(0);
  
  const slideImages = [
    { 
      src: salonMakeupImg,
      alt: "Salon Makeup",
      businessTypeId: 0
    },
    
    { 
      src: wellnessImg,
      alt: "Wellness Center",
      businessTypeId: 1
    },
    { 
      src: spaServiceImg,
      alt: "Spa Services",
      isServiceList: true,
      businessTypeId: 2
    },
    { 
      src: SalonManagementSoftware,
      alt: "Online Salon Management Software",
      businessTypeId: 3
    },
  ];

  const businessTypes: BusinessType[] = [
    {
      id: 0,
      name: "Beauty Salon",
      services: [
        "hair color", "bridal makeup", "pedicure", "skincare treatments"
      ]
    },
    {
      id: 1,
      name: "Wellness Center",
      services: [
        "yoga therapy", "meditation", "Ayurveda", "naturopathy"
      ]
    },
    {
      id: 2,
      name: "Spa Services",
      services: [
        "full-body massages", "aromatherapy", "deep tissue therapy"
      ]
    },
    {
      id: 3,
      name: "Salon & Spa Software",
      services: [
        "Online Booking",
        "Manage Appointments",
        "POS and Billing",
        "Inventory Management",

      ]
    },
  ];

  const handleArrowClick = (index: number): void => {
    // Now only used to close the services panel
    if (showServices && index === activeSlide) {
      setShowServices(false);
    }
  };

  const handleCardHover = (index: number): void => {
    setSelectedBusinessType(slideImages[index].businessTypeId);
    setActiveSlide(index);
    setShowServices(true);
  };

  const handleServiceClick = (service: string, businessTypeId?: number): void => {
    // For Salon & Spa Software services, redirect to pos.feelaxo.com/admin/register
    if (businessTypeId === 3) {
      window.location.href = "https://pos.feelaxo.com/admin/register";
    } else {
      // Continue with original behavior for other services
      navigate(`/listing?query=${encodeURIComponent(service)}`);
    }
  };

  return (
    <div className={`bg-red-400 p-4 ${className}`}>
      
      {/* Logo - Only visible on mobile */}
      <div className="flex justify-center md:hidden mt-4 mb-4">
        <img 
          src={FeelaxoLogoMobileOnly} 
          alt="Feelaxo Logo" 
          className="h-12 w-auto"
        />
      </div>
      
      {/* Heading */}
      <div className="max-w-3xl mt-8 mb-12 mx-auto text-center text-white">
        <h1 className="text-2xl md:text-4xl font-bold">
          Book Online Appointment <span className="font-normal">with Top-Rated</span> <br className="hidden md:block" />
          Salons and Spa Services <span className="font-normal">Near You</span>
        </h1>
        <p className="text-lg md:text-xl mb-8">
          Indulge in Pure Relaxation and Wellness Today!
        </p>
      </div>
      
        
        {/* Image Slider - Hidden on mobile, visible on md screens and up */}
        
      <div className="mx-auto my-8">
        <div className="grid grid-cols-2 md:flex md:flex-nowrap gap-4 overflow-x-auto pb-4 justify-center">
          {slideImages.map((image, index) => (
            <div 
              key={index}
              className={`relative rounded-lg overflow-hidden border-4 h-[250px] sm:h-[400px] md:h-[400px] md:w-[250px] md:flex-shrink-0 md:flex-none
                ${activeSlide === index && showServices ? "border-white" : "border-gray-200"}
                ${activeSlide === index ? "shadow-lg" : ""}`}
              onMouseEnter={() => handleCardHover(index)}
              onMouseLeave={() => setShowServices(false)}
            >
              <AnimatePresence>
                {(index !== activeSlide || !showServices) ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full w-full"
                  >
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                    <motion.div
                      className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 text-white p-2 sm:p-3"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[60%] sm:text-[90%]">{businessTypes[image.businessTypeId].name}</p>
                        <motion.button 
                          className={`${activeSlide === index && showServices ? "bg-black text-white" : "bg-white text-black"} rounded-full p-1 sm:p-2 z-10 shadow-md`}
                          onClick={() => handleArrowClick(index)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <motion.div
                            animate={{ rotate: activeSlide === index && showServices ? 180 : 0 }}
                          >
                            <FiChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                          </motion.div>
                        </motion.button>
                      </div>
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div 
                    className="h-full p-2 sm:p-4 flex flex-col bg-gray-100 relative"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ type: "spring", damping: 20 }}
                  >
                    <div className="flex justify-between items-center mb-2 sm:mb-4">
                      {businessTypes[selectedBusinessType].id === 3 ? (
                        <motion.div 
                        className="font-bold text-black text-xs sm:text-sm md:text-lg"
                        initial={{ y: -20 }}
                        animate={{ y: 0 }}
                      >
                        All in one Salon & Spa Management Software
                      </motion.div>
                      ) : (
                        <motion.div 
                        className="font-bold text-black text-xs sm:text-sm md:text-lg"
                        initial={{ y: -20 }}
                        animate={{ y: 0 }}
                      >
                        Book Online Appointment with best {businessTypes[selectedBusinessType].name}s near You
                      </motion.div>
                      )}
                      
                    </div>
                    <motion.div 
                      className="flex flex-col space-y-1 sm:space-y-2 text-black overflow-y-auto"
                      style={{ maxHeight: "200px", scrollbarWidth: "none" }}
                    >
                      {businessTypes[selectedBusinessType].services.map((service, i) => (
                        <motion.span 
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="py-1 px-2 sm:py-2 sm:px-3 bg-white hover:bg-gray-200 rounded-md cursor-pointer transition-all text-xs sm:text-sm border border-gray-300"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleServiceClick(service, selectedBusinessType)}
                        >
                          {service}
                        </motion.span>
                      ))}
                      
                    </motion.div>
                    <button 
                        className="absolute bottom-2 right-2 bg-black w-8 h-8 items-center flex justify-center text-white rounded-full p-1 sm:p-2 z-10 shadow-md"
                        onClick={() => handleArrowClick(index)}
                      >
                          <FiChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      

      {/* Search Form - Keeping the original HeroSearchForm */}
      <div className="max-w-7xl mx-auto">
        <HeroSearchForm className="text-white border-white" fill="rgb(255, 255, 255)" />
        {/* <div className="relative w-full mb-32">
          <KeywordListingMapFilters/>
        </div> */}
      </div>
    </div>
  );
};

export default SectionHero;